(() => {
  if (window.PoptexTemplateContent) return;
  const imageStates = new WeakMap();
  const imageRequests = new Map();
  const templateCache = new WeakMap();
  const imageRoles = [
    ['mainEffect', 'main_effect'], ['product', 'product'], ['usageScene', 'usage_scene'],
    ['sourceArtwork', 'source_artwork'], ['sourceArtworkBack', 'source_artwork_back']
  ];
  const safeImageUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, window.location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  };
  const images = (record) => {
    const seen = new Set();
    return imageRoles.flatMap(([field, label]) => {
      const url = safeImageUrl(record?.previewImages?.[field]);
      if (!url || seen.has(url)) return [];
      seen.add(url);
      return [{ url, label }];
    });
  };
  const publishedTemplates = (snapshot) => {
    if (!snapshot) return [];
    if (templateCache.has(snapshot)) return templateCache.get(snapshot);
    const records = (Array.isArray(snapshot?.patterns) ? snapshot.patterns : []).filter(p => p.productSeriesId && (p.status === 'PUBLISHED' || p.status === undefined)).map(p => ({ ...p, templateType: 'PATTERN', status: 'PUBLISHED', previewImages: { ...p.previewImages, mainEffect: p.previewImages?.mainEffect || p.effectUrl, sourceArtwork: p.previewImages?.sourceArtwork || p.sourceArtworkUrl, usageScene: p.previewImages?.scene, sourceArtworkBack: p.previewImages?.backArtwork } }));
    records.sort((a, b) => (Number(b.sortOrder) || 0) - (Number(a.sortOrder) || 0));
    templateCache.set(snapshot, records);
    return records;
  };
  const getTemplate = (snapshot, id, styleId) => {
    const patterns = publishedTemplates(snapshot);
    const linked = patterns.filter(p => p.legacyTemplateId === id);
    if (linked.length) return (styleId ? linked.find(p => p.productSeriesId === styleId) : linked.find(p => p.legacyDefault)) || null;
    return patterns.find(p => p.id === id) || null;
  };
  const eventTemplates = (snapshot, slug) => slug === 'new' ? publishedTemplates(snapshot) : [];
  const productPreview = (record) => {
    const previews = images(record);
    return previews.find((item) => item.label === 'product') || previews.find((item) => item.label === 'main_effect') || null;
  };
  // List imagery has its own role. Old caches intentionally resolve to no image.
  const displayPreview = record => {
    const url = safeImageUrl(record?.previewImages?.display);
    return url ? { url, label: 'display' } : null;
  };
  const newArrivals = (snapshot, limit = 8) => {
    const records = publishedTemplates(snapshot);
    const date = (record) => Date.parse(record.createdAt) || 0;
    const seen = new Set();
    return records.filter((record) => record?.id && record.status === 'PUBLISHED' && record.isNew === true)
      .slice().sort((a, b) => (Number(b.sortOrder) || 0) - (Number(a.sortOrder) || 0)
        || date(b) - date(a) || String(a.id).localeCompare(String(b.id)))
      .filter((record) => {
        if (seen.has(record.id)) return false;
        seen.add(record.id);
        return true;
      }).slice(0, Math.max(1, limit));
  };
  const detailUrl = (rootUrl, id) => {
    const url = new URL(rootUrl || '/', window.location.origin);
    url.searchParams.set('view', 'template-product');
    url.searchParams.set('pattern', id);
    return url.pathname + url.search;
  };
  const preloadImage = (url, priority = 'auto') => {
    const cached = imageRequests.get(url);
    if (cached) {
      if (priority === 'high') cached.image.fetchPriority = 'high';
      imageRequests.delete(url); imageRequests.set(url, cached);
      return cached.promise;
    }
    const image = new Image();
    image.decoding = 'async'; image.fetchPriority = priority;
    const entry = { image, settled: false };
    entry.promise = new Promise(resolve => {
      const finish = ok => { image.onload = image.onerror = null; resolve(ok); };
      image.onload = () => finish(true);
      image.onerror = () => finish(false);
      image.src = url;
    }).then(ok => {
      entry.settled = true;
      if (!ok) imageRequests.delete(url);
      for (const [key, item] of imageRequests) {
        if (imageRequests.size <= 32) break;
        if (item.settled) imageRequests.delete(key);
      }
      return ok;
    });
    imageRequests.set(url, entry);
    return entry.promise;
  };
  const setImage = (image, placeholder, value, options = {}) => {
    const url = safeImageUrl(value);
    const previous = imageStates.get(image);
    if (previous?.url === url && !previous.failed) return previous.promise;
    const state = { url, errorText: previous?.errorText ?? placeholder?.textContent ?? '' };
    imageStates.set(image, state);
    const finish = loaded => {
      if (imageStates.get(image) !== state) return false;
      const retained = options.retainOnError !== false && !loaded && Boolean(url && image.getAttribute('src') && image.complete && image.naturalWidth);
      image.hidden = !loaded && !retained;
      state.failed = !loaded;
      image.dataset.imageState = loaded ? 'ready' : 'error';
      if (placeholder) { placeholder.hidden = loaded || retained; placeholder.textContent = state.errorText; placeholder.dataset.imageState = image.dataset.imageState; placeholder.setAttribute('aria-busy', 'false'); }
      if (loaded) {
        if (image.getAttribute('src') !== url) image.src = url;
      } else if (!retained) image.removeAttribute('src');
      return loaded;
    };
    if (!url) { state.promise = Promise.resolve(finish(false)); return state.promise; }
    if (image.getAttribute('src') === url && image.complete && image.naturalWidth) {
      state.promise = Promise.resolve(finish(true)); return state.promise;
    }
    image.dataset.imageState = 'loading';
    const retained = Boolean(image.getAttribute('src') && image.complete && image.naturalWidth);
    image.hidden = !retained;
    if (placeholder) { placeholder.hidden = retained; placeholder.textContent = ''; placeholder.dataset.imageState = 'loading'; placeholder.setAttribute('aria-busy', 'true'); }
    state.promise = preloadImage(url, options.priority || image.fetchPriority || 'auto').then(finish);
    return state.promise;
  };
  const setDisplayImage = (image, placeholder, record, options = {}) => {
    if (placeholder) {
      placeholder.textContent = '◇';
      placeholder.classList.add('pattern-display-placeholder');
      placeholder.style.cssText = 'aspect-ratio:1;width:100%;background:#f4f2ef;color:#aaa;text-align:center;align-content:center;font-size:28px;';
      placeholder.setAttribute('aria-hidden', 'true');
    }
    return setImage(image, placeholder, displayPreview(record)?.url || '', { ...options, retainOnError: false });
  };
  const appendDisplayImage = (container, record) => {
    const image = document.createElement('img');
    image.alt = ''; image.width = 320; image.height = 320; image.loading = 'lazy';
    const placeholder = document.createElement('span');
    container.append(image, placeholder);
    void setDisplayImage(image, placeholder, record);
  };
  const watch = (root, render) => {
    const registry = window.PoptexContentRegistry;
    let disposed = false;
    let settled = !registry;
    let scheduled = false;
    let lastSnapshot, lastSettled;
    const update = () => {
      if (disposed) return;
      if (!root.isConnected) { dispose(); return; }
      const snapshot = registry?.getReadonlySnapshot?.() || registry?.getSnapshot?.() || null;
      if (snapshot === lastSnapshot && (snapshot || settled === lastSettled)) return;
      lastSnapshot = snapshot; lastSettled = settled;
      render(snapshot, settled);
    };
    const schedule = () => {
      if (scheduled || disposed) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; update(); });
    };
    const unload = (event) => {
      if (event.target === root || event.target.contains?.(root)) dispose();
    };
    const dispose = () => {
      disposed = true;
      window.removeEventListener('poptex:metadata-updated', schedule);
      document.removeEventListener('shopify:section:unload', unload);
    };
    window.addEventListener('poptex:metadata-updated', schedule);
    document.addEventListener('shopify:section:unload', unload);
    update();
    const finish = () => { settled = true; schedule(); };
    registry?.init?.().then(finish).catch(finish);
    return dispose;
  };
  window.PoptexTemplateContent = { images, publishedTemplates, eventTemplates, productPreview, displayPreview, setDisplayImage, appendDisplayImage, getTemplate, newArrivals, detailUrl, preloadImage, setImage, watch };
})();
